import { useCallback, useRef, useState } from "react";
import useApi from "./useApi";
import useSubscriptions from "./useSubscriptions";

// How many days is considered "large"? Falls back to the server default (90)
// if the subscription value isn't available yet.
const FALLBACK_THRESHOLD = 90;

// Max date range the server will ever accept synchronously (366 days hard cap).
// Above this we always use async regardless of subscription.
const HARD_SYNC_CAP = 366;

export type ExportStatus =
  | "idle"
  | "checking"      // determining sync vs async
  | "downloading"   // inline sync download in progress (≤ threshold days)
  | "queued"        // async export accepted (202), waiting for email
  | "polling"       // polling status endpoint after explicit ExportAsync call
  | "completed"     // async export done — downloadUrl available
  | "failed";

export interface ExportState {
  status: ExportStatus;
  exportId?: number;
  downloadUrl?: string;
  expiresAt?: string;
  recipientEmail?: string;
  errorMessage?: string;
}

export interface ExportOptions {
  startDate: Date;
  endDate: Date;
  reportType?: "account-overview" | "agent-utilization" | "compliance";
  format?: "xlsx" | "csv";
  /** Override recipient email. Defaults to authenticated user's email. */
  recipientEmail?: string;
}

/**
 * Handles both sync (inline download) and async (email + polling) report exports.
 *
 * Usage:
 *   const { exportState, startExport, reset } = useReportExport();
 *
 * The hook decides automatically:
 *   - Range ≤ subscription.maxExportDays (default 90) → sync: GET blob, browser downloads
 *   - Range >  threshold → async: POST ExportAsync, shows "email on its way" UI
 *
 * For the sync path, pass a 30-second abort signal to avoid hanging the UI on slow
 * report queries. The server will also flip to async if the range exceeds the threshold
 * server-side, so double-protection is in place.
 */
export function useReportExport() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [exportState, setExportState] = useState<ExportState>({ status: "idle" });
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const threshold =
    subscription?.maxExportDays != null && subscription.maxExportDays > 0
      ? subscription.maxExportDays
      : FALLBACK_THRESHOLD;

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setExportState({ status: "idle" });
  }, [stopPolling]);

  const pollStatus = useCallback(
    (exportId: number) => {
      stopPolling();
      setExportState((s) => ({ ...s, status: "polling" }));

      pollTimerRef.current = setInterval(async () => {
        try {
          const res = await api.admin.get(
            `Reports/ExportStatus/${exportId}`,
            undefined,
            AbortSignal.timeout(10_000)
          );
          if (!res.ok) return; // transient — keep polling

          const data = await res.json();

          if (data.status === "completed") {
            stopPolling();
            setExportState({
              status: "completed",
              exportId,
              downloadUrl: data.downloadUrl,
              expiresAt: data.expiresAt
            });
          } else if (data.status === "failed") {
            stopPolling();
            setExportState({
              status: "failed",
              exportId,
              errorMessage: data.errorMessage ?? "Export failed. Please try again."
            });
          }
          // "pending" or "processing" → keep polling
        } catch {
          // Network blip — keep polling silently
        }
      }, 4_000); // poll every 4 seconds
    },
    [api.admin, stopPolling]
  );

  const startExport = useCallback(
    async (options: ExportOptions) => {
      stopPolling();
      setExportState({ status: "checking" });

      const dayRange =
        (options.endDate.getTime() - options.startDate.getTime()) / (1000 * 60 * 60 * 24);

      const isLargeRange = dayRange > threshold || dayRange > HARD_SYNC_CAP;

      if (isLargeRange) {
        // ── Async path: POST ExportAsync, server queues background job ──────
        try {
          const res = await api.admin.post(
            "Reports/ExportAsync",
            {
              startDate: options.startDate.toISOString(),
              endDate: options.endDate.toISOString(),
              reportType: options.reportType ?? "account-overview",
              format: options.format ?? "xlsx",
              recipientEmail: options.recipientEmail
            },
            AbortSignal.timeout(15_000)
          );

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setExportState({
              status: "failed",
              errorMessage: body.error ?? "Failed to queue export."
            });
            return;
          }

          const data = await res.json();
          setExportState({
            status: "queued",
            exportId: data.exportId,
            recipientEmail: data.recipientEmail
          });

          // Start polling so the UI can update if the user stays on the page.
          if (data.exportId) pollStatus(data.exportId);
        } catch {
          setExportState({ status: "failed", errorMessage: "Network error. Please try again." });
        }
      } else {
        // ── Sync path: POST Reports/Generate, trigger browser download ───────
        // Uses a 30-second abort signal — generous for slow queries, won't hang forever.
        setExportState({ status: "downloading" });
        try {
          const res = await api.admin.post(
            "Reports/Generate",
            {
              startDate: options.startDate.toISOString(),
              endDate: options.endDate.toISOString(),
              format: options.format ?? "xlsx"
            },
            AbortSignal.timeout(30_000)
          );

          if (!res.ok) {
            // Server may have decided the range is too large and returned 202 (async redirect).
            if (res.status === 202) {
              const data = await res.json().catch(() => ({}));
              setExportState({
                status: "queued",
                exportId: data.exportId,
                recipientEmail: data.recipientEmail
              });
              if (data.exportId) pollStatus(data.exportId);
              return;
            }
            const body = await res.json().catch(() => ({}));
            setExportState({
              status: "failed",
              errorMessage: body.error ?? "Report generation failed."
            });
            return;
          }

          // Trigger browser file download from the response blob.
          const blob = await res.blob();
          const contentDisposition = res.headers.get("Content-Disposition") ?? "";
          const filenameMatch = contentDisposition.match(/filename="?([^";\r\n]+)"?/i);
          const filename = filenameMatch?.[1] ?? `Velaro-Report.${options.format ?? "xlsx"}`;

          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          setExportState({ status: "idle" }); // reset after successful download
        } catch {
          setExportState({
            status: "failed",
            errorMessage: "Download timed out or failed. Try a shorter date range, or use the email export option."
          });
        }
      }
    },
    [api.admin, threshold, pollStatus, stopPolling]
  );

  return { exportState, startExport, reset, threshold };
}
